import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcGenerateV1TradeConfirmationsTaskQueryServiceTradeConfirmationsTaskGet } from '@interxfi/api-client/generated/hooks/ixc-generate-v1-trade-confirmations-task-query-service-trade-confirmations-task-get';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import type { IxcGenerateV1TradeConfirmationsTaskState } from '@interxfi/types/generated/api';
import { useDownloadDocument } from '../../../../../../../../../../../features/document/utils';
import {
  DocgenTradeConfirmTaskEventDrawer,
  DocgenTradeConfirmTaskEventSummary,
} from '../../../../../../../../../../../features/generate/trade-confirmation/task/docgen-trade-confirm-task-event';
import { getDocgenTradeConfirmTaskStateListItems } from '../../../../../../../../../../../features/generate/trade-confirmation/task/docgen-trade-confirm-task-list-items';
import { getDocgenTradeConfirmationTaskDetailPath } from '../../../../../../../../../../../features/generate/trade-confirmation/task/docgen-trade-confirm-task-routing';
import { TradeConfirmTaskStatusPill } from '../../../../../../../../../../../features/generate/trade-confirmation/task/trade-confirm-task-status-pill';
import { ProgramLogomark } from '../../../../../../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/generate/trade-confirmation/task/_task/$taskId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `trade-confirmation-task-${params.taskId}`,
        label: 'Task',
        to: getDocgenTradeConfirmationTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Trade Confirmation Task'),
  component: DocgenTradeConfirmTaskDetail,
});

function getListItems(data: IxcGenerateV1TradeConfirmationsTaskState | undefined) {
  const items = getDocgenTradeConfirmTaskStateListItems(data);

  return {
    general: {
      left: [
        items.data.spec.tradeDate,
        items.data.spec.revision,
        items.data.totalTransactions,
        ...(items.data.documentDetails
          ? [items.data.documentDetails.documentIdWithActions, items.data.documentDetails.bucket, items.data.documentDetails.key]
          : []),
      ],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.taskId, items.jobId, items.accountId, items.programId],
    },
  };
}

function getMenuItems(
  taskId: string,
  documentId: string | undefined,
  handleDownload: (docId: string) => Promise<void>,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: { Component: DocgenTradeConfirmTaskEventDrawer, props: { request: { taskId } } },
    },
    actions: documentId
      ? [
          {
            id: 'download',
            icon: <DownloadIcon aria-hidden />,
            label: 'Download',
            disabled: isDownloading,
            onClick: async () => {
              try {
                await handleDownload(documentId);
              } catch {}
            },
          },
        ]
      : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load trade confirmation task', 'view this trade confirmation task');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter(
  'Failed to download trade confirmation document',
  'view this trade confirmation document',
);

function DocgenTradeConfirmTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { taskId } as const;
  const { data, error, isPending } = useIxcGenerateV1TradeConfirmationsTaskQueryServiceTradeConfirmationsTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.tradeConfirmationsTask), [data?.tradeConfirmationsTask]);
  const { handleDownload, isPending: isDownloading, error: downloadError } = useDownloadDocument();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const menuItems = useMemo(
    () => getMenuItems(taskId, data?.tradeConfirmationsTask?.data?.documentDetails?.documentId, handleDownload, isDownloading),
    [taskId, data?.tradeConfirmationsTask?.data?.documentDetails?.documentId, handleDownload, isDownloading],
  );
  const { activeTenantContext } = useActiveRealmAPIContext();

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            {activeTenantContext !== TenantContext.Program ? (
              <ProgramLogomark programId={data?.tradeConfirmationsTask?.programId} shouldLoadLogoMark size="large" withTooltip />
            ) : null}
            <TradeConfirmTaskStatusPill isPending={showPending} size="large" status={data?.tradeConfirmationsTask?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <DocgenTradeConfirmTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
